import React, { RefObject, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import FileSaver from 'file-saver';
import moment from 'moment';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { getUsersAction, updateUser } from '../../../store/admin/file-type-configuration/actions';
import DownloadIcon from '../../../assets/icons/download-icon';
import useStyles from './styles';
import { tableHeadCells, tableSortableAccessors } from './head-cell';
import AcceptIcon from '../../../assets/icons/accept-icon';
import CancelIcon from '../../../assets/icons/cancel-icon';
import Table from '../table';
import { enqueueSnackbarAction } from '../../../store/snackbar/actions';

type Users = {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  catrecid: string;
  lastLoginTime: string;
  createdTime: string;
}

const UserAccessList = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [users, setUsers] = useState<Users[]>([]);
  const tableContainer: RefObject<HTMLDivElement> = useRef(null);

  const [columnVisibility, setColumnVisibility] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [confirmationBox, setConfirmationBox] = useState<boolean>(false);
  const [updateFlag, setUpdateFlag] = useState<boolean>(false);
  const [reset, setReset] = useState<boolean>(false);

  const TableData = {
    title: 'User List',
    storageString: 'user-access-list',
    headerColumns: tableHeadCells,
    sortableAccessors: tableSortableAccessors,
    onRowSelection: setSelectedRows,
    data: users,
    id: 'user-access-list',
    setColumnVisibility,
    columnVisibility,
    setColumnOrder,
    columnOrder,
    tableContainer,
    headerCheckbox: false,
  };


  /* eslint-disable unicorn/consistent-function-scoping */
  const getEntitlements = (entitlements: any) => {
    const roles = entitlements.map((entitlement: any) => {
      return entitlement?.roles.length > 0 ? entitlement?.roles[0] : [];
    });
    const entitlementsData = roles.map((role: any) => {
      return role.roleName;
    });
    return entitlementsData.join(",");
  };

  const getCreatedTime = (entitlements: any) => {
    const createdTime = entitlements.map((entitlement: any) => {
      return entitlement?.createdTime ? moment(entitlement?.createdTime).format('YYYY/MM/DD') : "";
    });
    return createdTime.join(",");
  };

  const getUsers = async () => {
    let users: any = await dispatch(getUsersAction());
    /* eslint arrow-body-style: ["error", "always"] */
    users = users.map((user: any) => {
      return {
        ...user, lastLoginTime: user.applications[0]?.entitlements[0]?.lastLoginTime ? moment(user.applications[0]?.entitlements[0]?.lastLoginTime).format('YYYY/MM/DD') : "", entitlements: getEntitlements(user.applications[0]?.entitlements || []),
        createdTime: getCreatedTime(user.applications[0]?.entitlements || [])
      }
    });
    setUsers(users);
  }

  function exportUserList() {
    const userHeaderString = 'First Name, Last Name, Email, Username, Entitlements, Last Login, Created Time';
    const userAccessors = [
      `firstName`,
      'lastName',
      'email',
      "username",
      "entitlements",
      "lastLoginTime",
      "createdTime"
    ];
    try {
      /* eslint arrow-body-style: ["off", "always"] */
      const rowAsString = users.map((row: any) => `${userAccessors.map((accessor: string) => `${row[accessor] || ''}`)}\n`);
      const blob = new Blob([`${userHeaderString}\n${rowAsString.join('')}`], {
        type: 'text/plain;charset=utf-8',
      });
      FileSaver.saveAs(blob, `User_List_${moment().format('MMMM_Do_YYYY_h:mm:ss_a')}.csv`);
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    getUsers();

  }, [updateFlag]);

  const openConfirmBox = () => {
    setConfirmationBox(true);
  }

  const cancelRevokeAction = () => {
    setReset(true);
    setConfirmationBox(false);
    setTimeout(() => { setReset(false); }, 50)
  }

  const handleConfirmation = async () => {
    const catrecIds = selectedRows && selectedRows.map((row: any) => { return (row?.catrecid) });
    catrecIds.forEach(async (id) => {
      await dispatch(updateUser({ catrecId: id, showMessage: false }))
      setUpdateFlag(true);
    });
    setConfirmationBox(false);
    setSelectedRows([]);
    dispatch(
      enqueueSnackbarAction({
        message: 'Selected Users are successfully removed',
        options: { variant: 'success' },
      }),
    );
  }

  return (
    <div>
      <Box display="flex" flexDirection="row">
        <Button
          className={classes.revokeButton}
          variant="outlined"
          color="primary"
          disabled={selectedRows.length === 0}
          sx={{ marginBottom: "10px" }}
          onClick={openConfirmBox}
        >
          Revoke Users
        </Button>
        <Button
          disabled={users.length === 0}
          className={classes.revokeButton}
          onClick={exportUserList}
          variant="outlined"
          size="small"
          sx={{ marginBottom: "10px", marginLeft: 2 }}
        >
          Export <DownloadIcon className={classes.inButtonIcon} />
        </Button>
        {selectedRows.length > 0 && confirmationBox && (
          <Box display="flex" flexDirection="row" sx={{ ml: 2, mt: 1 }}>
            <Typography>Are you sure ?</Typography>
            <IconButton size="large" onClick={handleConfirmation} sx={{ mt: -2 }}>
              <AcceptIcon />
            </IconButton>
            <IconButton
              onClick={cancelRevokeAction}
              aria-label="edit"
              size="large"
              sx={{ mt: -2 }}
            >
              <CancelIcon />
            </IconButton>
          </Box>
        )}
      </Box>
      <Box width="100%" display="flex" flexDirection="column">
        <Box flexShrink={1}>
          {!reset && (
            <div ref={tableContainer} className={classes.tableContainer}>
              <Table {...TableData} />
            </div>
          )}
        </Box>
      </Box>
    </div>
  );
};

export default UserAccessList;

